import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { billingApi } from '../../../services/billingApi';
import { currentUserHasCerebralAdminRole } from '../../../utils/roleCheck';
import {
  Alert,
  Button,
  Checkbox,
  Select,
  Textarea,
  TextInput,
} from '../../../components/common';
import { SectionCard, StatusBadge, WizardStepper } from './components';
import './BillingAdmin.css';

const STEPS = [
  'Installation identity',
  'Reporting',
  'Subscriptions',
  'Review & provision',
];

const ENVIRONMENTS = [
  { value: 'production', label: 'Production' },
  { value: 'staging', label: 'Staging' },
  { value: 'dev', label: 'Development' },
];

const INTERVAL_PRESETS = [
  { id: 300, label: '5m' },
  { id: 900, label: '15m' },
  { id: 3600, label: '1h' },
  { id: 21600, label: '6h' },
];

const BILLING_CYCLES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

function isValidUuid(v) {
  return !!v && UUID_RE.test(String(v).trim());
}

function isValidUrl(v) {
  if (!v) return false;
  try { new URL(v); return true; } catch { return false; }
}

function CopyIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

export default function OnboardCustomerPage() {
  const navigate = useNavigate();
  const [pricingPlans, setPricingPlans] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [step, setStep] = useState(0);
  const [stepError, setStepError] = useState(null);

  const [form, setForm] = useState({
    label: '',
    companyId: '',
    environment: 'production',
    reportIntervalSeconds: 3600,
    disruptionThreshold: 3,
    controlPlaneUrl: typeof window !== 'undefined' ? window.location.origin : '',
    notes: '',
  });
  const [subs, setSubs] = useState([
    { companyAccountId: '', pricingPlanId: '', billingCycle: 'monthly' },
  ]);

  // success-panel state
  const [revealed, setRevealed] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  const [envCopied, setEnvCopied] = useState(false);

  useEffect(() => {
    if (!currentUserHasCerebralAdminRole()) navigate('/user-home');
  }, [navigate]);

  useEffect(() => {
    (async () => {
      try {
        const data = await billingApi.listPricingPlans();
        setPricingPlans(data?.plans || data?.items || []);
      } catch {
        // non-fatal
      }
    })();
  }, []);

  const planOptions = useMemo(
    () => pricingPlans.map((p) => ({
      value: p.PricingPlanId || p.pricing_plan_id,
      label: p.Name || p.name,
    })),
    [pricingPlans]
  );

  const updateSub = (idx, patch) => setSubs(subs.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  const addSub = () => setSubs([...subs, { companyAccountId: '', pricingPlanId: '', billingCycle: 'monthly' }]);
  const removeSub = (idx) => setSubs(subs.filter((_, i) => i !== idx));

  const validateStep = (s) => {
    if (s === 0) {
      if (!form.label.trim()) return 'Label is required.';
      if (form.companyId && !isValidUuid(form.companyId)) return 'Company ID must be a valid UUID or left blank.';
      if (!isValidUrl(form.controlPlaneUrl)) return 'Control plane URL must be a valid URL (e.g. https://cerebralmachines.ai).';
      return null;
    }
    if (s === 1) {
      const ri = Number(form.reportIntervalSeconds);
      if (!isFinite(ri) || ri < 60) return 'Report interval must be at least 60 seconds.';
      const dt = Number(form.disruptionThreshold);
      if (!isFinite(dt) || dt < 1) return 'Disruption threshold must be a positive integer.';
      return null;
    }
    if (s === 2) {
      const real = subs.filter((sub) => sub.companyAccountId || sub.pricingPlanId);
      for (const sub of real) {
        if (!isValidUuid(sub.companyAccountId)) return 'Each subscription needs a valid Account UUID.';
        if (!sub.pricingPlanId) return 'Each subscription needs a pricing plan selected.';
      }
      return null;
    }
    return null;
  };

  const goNext = () => {
    const err = validateStep(step);
    if (err) {
      setStepError(err);
      return;
    }
    setStepError(null);
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const goBack = () => {
    setStepError(null);
    setStep((s) => Math.max(s - 1, 0));
  };

  const submit = async () => {
    for (let s = 0; s <= 2; s += 1) {
      const err = validateStep(s);
      if (err) { setStepError(err); setStep(s); return; }
    }
    setBusy(true);
    setError(null);
    setStepError(null);
    try {
      const payload = {
        ...form,
        reportIntervalSeconds: Number(form.reportIntervalSeconds),
        disruptionThreshold: Number(form.disruptionThreshold),
        companyId: form.companyId || null,
        notes: form.notes || null,
        subscriptions: subs.filter((s) => s.companyAccountId && s.pricingPlanId),
      };
      const data = await billingApi.onboardCustomer(payload);
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Onboarding failed');
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    const continueDisabled = !acknowledged;
    const copy = async (text, setter) => {
      try {
        await navigator.clipboard?.writeText(text);
        setter(true);
        setTimeout(() => setter(false), 1400);
      } catch { /* ignore */ }
    };
    return (
      <div className="user-home-layout__page" style={{ maxWidth: 880 }}>
        <Link to="/user-home/admin/billing" className="billing-admin__breadcrumb">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
          Back to dashboard
        </Link>

        <div className="billing-admin__page-header">
          <div className="billing-admin__header-text">
            <h1 className="user-home-layout__page-title">
              Provisioning complete
            </h1>
            <p className="user-home-layout__page-subtitle" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <StatusBadge status="active" label="Installation provisioned" />
              <span>Save the HMAC secret below before leaving this page.</span>
            </p>
          </div>
        </div>

        <Alert variant="warning" title="One-time secret">
          The HMAC secret is shown <strong>once</strong>. Copy the snippet below into{' '}
          <code>/opt/cm/config/backend.env</code> on the customer cloud now. If you lose it, you must
          re-provision the install.
        </Alert>

        <SectionCard title="Installation ID" subtitle="Send this to the customer along with the .env snippet.">
          <div className="billing-admin__id-pill" style={{ maxWidth: '100%' }}>
            <CopyIcon /> {result.installationId}
          </div>
        </SectionCard>

        <SectionCard title="HMAC secret">
          <div className={`billing-admin__secret ${revealed ? '' : 'billing-admin__secret--masked'}`}>
            <div className="billing-admin__secret-toolbar">
              <span className="billing-admin__secret-label">HMAC secret (hex)</span>
              <div className="billing-admin__secret-actions">
                <button type="button" className="billing-admin__secret-btn" onClick={() => setRevealed((v) => !v)}>
                  {revealed ? 'Hide' : 'Reveal'}
                </button>
                <button type="button" className="billing-admin__secret-btn" onClick={() => copy(result.hmacSecret, setSecretCopied)}>
                  {secretCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            <pre>{result.hmacSecret}</pre>
          </div>
        </SectionCard>

        <SectionCard
          title=".env snippet"
          subtitle="Drop this into /opt/cm/config/backend.env on the customer cloud."
          toolbar={
            <>
              <Button variant="secondary" onClick={() => copy(result.envSnippet, setEnvCopied)}>
                {envCopied ? 'Copied!' : 'Copy snippet'}
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  const blob = new Blob([result.envSnippet], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `cm-installation-${result.installationId}.env`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                Download .env
              </Button>
            </>
          }
        >
          <pre className="billing-admin__code-block">{result.envSnippet}</pre>
        </SectionCard>

        <SectionCard>
          <Checkbox
            label="I have saved the HMAC secret in a safe place (password manager, secret store, etc)."
            checked={acknowledged}
            onChange={(e) => setAcknowledged(!!e?.target?.checked)}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <Link to="/user-home/admin/billing">
              <Button variant="ghost">Back to dashboard</Button>
            </Link>
            <Link to={`/user-home/admin/billing/installations/${result.installationId}`}>
              <Button variant="primary" disabled={continueDisabled}>
                Open installation →
              </Button>
            </Link>
          </div>
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="user-home-layout__page" style={{ maxWidth: 880 }}>
      <Link to="/user-home/admin/billing" className="billing-admin__breadcrumb">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
        Back to dashboard
      </Link>

      <div className="billing-admin__page-header">
        <div className="billing-admin__header-text">
          <h1 className="user-home-layout__page-title">Onboard a customer installation</h1>
          <p className="user-home-layout__page-subtitle">
            Provisions a new <code>Installation</code>, creates the requested subscriptions, and returns
            a one-time HMAC secret + <code>.env</code> snippet to install on the customer cloud.
          </p>
        </div>
      </div>

      <SectionCard>
        <WizardStepper steps={STEPS} current={step} />

        {stepError && <Alert variant="error">{stepError}</Alert>}
        {error && <Alert variant="error" title="Onboarding failed">{error}</Alert>}

        {step === 0 && (
          <div className="billing-admin__form-grid">
            <TextInput
              variant="modal"
              label="Label *"
              hint="Shown in the dashboard. Pick something distinctive like 'Acme Production'."
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Acme Production"
              required
            />
            <Select
              variant="modal"
              label="Environment"
              value={form.environment}
              onChange={(e) => setForm({ ...form, environment: e.target.value })}
              options={ENVIRONMENTS}
              placeholder=""
            />
            <TextInput
              variant="modal"
              label="Company ID (optional)"
              hint="UUID of an existing Company. Leave blank to provision a standalone install."
              value={form.companyId}
              onChange={(e) => setForm({ ...form, companyId: e.target.value })}
              placeholder="00000000-0000-0000-0000-000000000000"
            />
            <TextInput
              variant="modal"
              label="Control plane URL *"
              hint="The customer cloud will report usage to this URL."
              value={form.controlPlaneUrl}
              onChange={(e) => setForm({ ...form, controlPlaneUrl: e.target.value })}
              placeholder="https://cerebralmachines.ai"
            />
            <div style={{ gridColumn: '1 / -1' }}>
              <Textarea
                variant="modal"
                label="Notes (internal)"
                hint="Surfaced only to CerebralAdmin operators."
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="billing-admin__form-grid">
            <div>
              <TextInput
                variant="modal"
                label="Report interval (seconds) *"
                hint="How often the metering agent uploads usage."
                type="number"
                min="60"
                value={form.reportIntervalSeconds}
                onChange={(e) => setForm({ ...form, reportIntervalSeconds: e.target.value })}
              />
              <div className="billing-admin__preset-row" style={{ marginTop: 8 }}>
                {INTERVAL_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={`billing-admin__preset-chip ${Number(form.reportIntervalSeconds) === p.id ? 'is-active' : ''}`}
                    onClick={() => setForm({ ...form, reportIntervalSeconds: p.id })}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <TextInput
              variant="modal"
              label="Disruption threshold *"
              hint="Number of missed report intervals before raising an alert."
              type="number"
              min="1"
              value={form.disruptionThreshold}
              onChange={(e) => setForm({ ...form, disruptionThreshold: e.target.value })}
            />
          </div>
        )}

        {step === 2 && (
          <>
            <p className="billing-admin__muted" style={{ marginTop: 0 }}>
              Map each CompanyAccount on this install to a pricing plan and billing cycle. Leave a row
              blank to skip; you can always add more later from the installation detail page.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {subs.map((sub, idx) => (
                <div key={idx} className="billing-admin__form-row billing-admin__form-row--lineitem">
                  <TextInput
                    variant="modal"
                    placeholder="CompanyAccount UUID"
                    value={sub.companyAccountId}
                    onChange={(e) => updateSub(idx, { companyAccountId: e.target.value })}
                  />
                  <Select
                    variant="modal"
                    value={sub.pricingPlanId}
                    onChange={(e) => updateSub(idx, { pricingPlanId: e.target.value })}
                    options={planOptions}
                    placeholder="Pricing plan…"
                  />
                  <Select
                    variant="modal"
                    value={sub.billingCycle}
                    onChange={(e) => updateSub(idx, { billingCycle: e.target.value })}
                    options={BILLING_CYCLES}
                    placeholder=""
                  />
                  <span />
                  <Button
                    variant="ghost"
                    onClick={() => removeSub(idx)}
                    disabled={subs.length === 1}
                  >
                    ×
                  </Button>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12 }}>
              <Button variant="secondary" onClick={addSub}>+ Add subscription</Button>
            </div>
          </>
        )}

        {step === 3 && (
          <div>
            <h3 className="billing-admin__section-title" style={{ marginTop: 0 }}>Review</h3>
            <dl className="billing-admin__dl">
              <dt>Label</dt><dd>{form.label || <em>(unset)</em>}</dd>
              <dt>Environment</dt><dd>{form.environment}</dd>
              <dt>Company ID</dt><dd>{form.companyId || <span className="billing-admin__muted">— standalone install —</span>}</dd>
              <dt>Control plane URL</dt><dd><code>{form.controlPlaneUrl}</code></dd>
              <dt>Report interval</dt><dd>{form.reportIntervalSeconds}s</dd>
              <dt>Disruption threshold</dt><dd>{form.disruptionThreshold} missed intervals</dd>
              <dt>Notes</dt><dd>{form.notes || <span className="billing-admin__muted">—</span>}</dd>
            </dl>

            <hr className="billing-admin__divider" />

            <h4 className="billing-admin__section-title" style={{ fontSize: 'var(--uh-font-size-base)' }}>
              Subscriptions ({subs.filter((s) => s.companyAccountId && s.pricingPlanId).length})
            </h4>
            {subs.filter((s) => s.companyAccountId && s.pricingPlanId).length === 0 && (
              <p className="billing-admin__muted">No subscriptions will be created. You can add them later.</p>
            )}
            {subs.filter((s) => s.companyAccountId && s.pricingPlanId).length > 0 && (
              <table className="billing-admin__table" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>Account UUID</th>
                    <th>Plan</th>
                    <th>Cycle</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.filter((s) => s.companyAccountId && s.pricingPlanId).map((s, idx) => {
                    const plan = pricingPlans.find((p) => (p.PricingPlanId || p.pricing_plan_id) === s.pricingPlanId);
                    return (
                      <tr key={idx}>
                        <td><code>{s.companyAccountId}</code></td>
                        <td>{plan ? (plan.Name || plan.name) : s.pricingPlanId}</td>
                        <td><span className="billing-admin__kind-chip">{s.billingCycle}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        <div className="billing-admin__wizard-actions">
          {step > 0 ? (
            <Button variant="ghost" onClick={goBack} disabled={busy}>← Back</Button>
          ) : <span />}
          {step < STEPS.length - 1 ? (
            <Button variant="primary" onClick={goNext} disabled={busy}>Next →</Button>
          ) : (
            <Button variant="primary" onClick={submit} disabled={busy}>
              {busy ? 'Provisioning…' : 'Provision installation'}
            </Button>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
